import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

/**
 * Stable wire discriminators for server-thrown `I18nError`s. The string values cross the
 * server→client boundary as the error `message`; the client renders the matching descriptor from
 * `AUTH_ERROR_MESSAGES`. Add a new key here and a matching entry in `AUTH_ERROR_MESSAGES` — the
 * `Record<I18nErrorKey, ...>` type makes the descriptor mandatory at compile time.
 */
export const I18N_ERROR_KEYS = [
	"auth:email-verification.errors.missing-verification-id",
	"auth:login.errors.incorrect-email-or-password",
	"auth:login.errors.external-organization",
	"auth:reset-password.errors.invalid-or-expired-link",
	"auth:signup.errors.email-already-exists",
] as const;
export type I18nErrorKey = (typeof I18N_ERROR_KEYS)[number];

export const AUTH_ERROR_MESSAGES: Record<I18nErrorKey, MessageDescriptor> = {
	"auth:email-verification.errors.missing-verification-id": msg`Missing email verification ID.`,
	"auth:login.errors.incorrect-email-or-password": msg`Incorrect email or password. Please try again.`,
	"auth:login.errors.external-organization": msg`This account can't be used with other organizations. Create an account with another email or contact your administrator.`,
	"auth:reset-password.errors.invalid-or-expired-link": msg`This password reset link is no longer valid. It may have expired or already been used.`,
	"auth:signup.errors.email-already-exists": msg`An account already exists for this email. Log in or reset your password to continue.`,
};

export class I18nError extends Error {
	public readonly i18nKey: I18nErrorKey;

	constructor(i18nKey: I18nErrorKey) {
		super(i18nKey);
		this.name = "I18nError";
		this.i18nKey = i18nKey;
	}
}
